// src/pages/Admin/Adherents/components/EditAdherentDrawer.jsx

import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  FaCloudUploadAlt,
  FaCropAlt,
  FaImage,
  FaLink,
  FaSave,
  FaTimes,
} from "react-icons/fa";
import { toast } from "react-toastify";

import AdherentDrawer from "./AdherentDrawer";

import { updateAdherent } from "../../../../../services/adherentServices";
import { getSettings } from "../../../../../services/settingsServices";
import {
  cropAdherentPhoto,
  toAbsolutePhotoUrl,
  uploadAdherentPhoto,
} from "../../../../../services/adherentPhotoServices";

import CropPhotoModal, {
  toMasterUrl,
} from "../../../../../components/CropPhotoModal/CropPhotoModal";

const initialState = {
  nom: "",
  prenom: "",
  email: "",
  email_secondaire: "",
  tel: "",
  tel_secondaire: "",
  representant_legal: "",
  adresse: "",
  cp: "",
  commune: "",
  genre: "",
  statut_inscription: "",
  date_naissance: "",
  lieu_naissance: "",
  photo_url: "",
  licence_number: "",
  poids: "",
  saison_active: "",
  notes_adherent: "",
  autorisation_photo: false,
  passsport_number: "",
  createdAt: "",
};

function getPreviousSeason(currentSeason = "") {
  const [y1, y2] = String(currentSeason).split("-").map(Number);
  if (!y1 || !y2) return "";
  return `${y1 - 1}-${y2 - 1}`;
}

function getNextSeason(currentSeason = "") {
  const [y1, y2] = String(currentSeason).split("-").map(Number);
  if (!y1 || !y2) return "";
  return `${y1 + 1}-${y2 + 1}`;
}

function sanitizeDateForInput(raw) {
  if (!raw) return { value: "", invalid: false };

  const value = String(raw).trim();

  if (value === "0000-00-00") {
    return { value: "", invalid: true };
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return { value: "", invalid: true };
  }

  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");

  return {
    value: `${yyyy}-${mm}-${dd}`,
    invalid: false,
  };
}

function isValidDateInput(value) {
  if (!value) return true;

  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;

  const [year, month, day] = value.split("-").map(Number);
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return false;

  return (
    date.getFullYear() === year &&
    date.getMonth() + 1 === month &&
    date.getDate() === day
  );
}

function withBust(url, version) {
  if (!url) return url;
  return url.includes("?") ? `${url}&v=${version}` : `${url}?v=${version}`;
}

function FieldLabel({ children }) {
  return (
    <label className="mb-1 block text-sm font-bold text-slate-700 dark:text-slate-200">
      {children}
    </label>
  );
}

function SectionCard({ icon, title, subtitle, children }) {
  return (
    <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900/60">
      <div className="mb-4 flex items-center gap-3">
        <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300">
          {icon}
        </span>

        <div>
          <h3 className="font-bold text-slate-900 dark:text-white">{title}</h3>
          {subtitle ? (
            <p className="text-sm text-slate-600 dark:text-slate-300">
              {subtitle}
            </p>
          ) : null}
        </div>
      </div>

      {children}
    </div>
  );
}

export default function EditAdherentDrawer({
  isOpen,
  onClose,
  refreshList,
  adherent,
}) {
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState(initialState);
  const [seasons, setSeasons] = useState([]);

  const [saving, setSaving] = useState(false);
  const [imgOk, setImgOk] = useState(true);
  const [invalidBirthDate, setInvalidBirthDate] = useState(false);

  const [uploadMode, setUploadMode] = useState("url");
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [outputFormat, setOutputFormat] = useState("webp");
  const [imgVersion, setImgVersion] = useState(0);

  const [isCropOpen, setIsCropOpen] = useState(false);

  const loadSeasons = useCallback(async () => {
    try {
      const settingsData = await getSettings();
      const settings = Array.isArray(settingsData) ? settingsData[0] : settingsData;
      const active = settings?.saison_active || "";

      setSeasons(
        [getPreviousSeason(active), active, getNextSeason(active)].filter(Boolean)
      );
    } catch (error) {
      console.error("Erreur chargement saisons :", error);
      toast.error("Erreur lors du chargement des saisons.");
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    loadSeasons();
  }, [isOpen, loadSeasons]);

  useEffect(() => {
    if (!isOpen || !adherent) return;

    const birth = sanitizeDateForInput(adherent.date_naissance);
    const created = sanitizeDateForInput(adherent.createdAt || adherent.created_at);

    setFormData({
      nom: adherent.nom || "",
      prenom: adherent.prenom || "",
      email: adherent.email || "",
      email_secondaire: adherent.email_secondaire || "",
      tel: adherent.tel || "",
      tel_secondaire: adherent.tel_secondaire || "",
      representant_legal: adherent.representant_legal || "",
      adresse: adherent.adresse || "",
      cp: adherent.cp || "",
      commune: adherent.commune || "",
      genre: adherent.genre || "",
      statut_inscription: adherent.statut_inscription || "",
      date_naissance: birth.value,
      lieu_naissance: adherent.lieu_naissance || "",
      photo_url: adherent.photo_url || "",
      licence_number: adherent.licence_number || "",
      poids: adherent.poids || "",
      saison_active: adherent.saison_active || "",
      notes_adherent: adherent.notes_adherent || "",
      autorisation_photo: Boolean(adherent.autorisation_photo),
      passsport_number: adherent.passsport_number || "",
      createdAt: created.value,
    });

    setInvalidBirthDate(birth.invalid);
    setImgOk(true);
    setUploadMode("url");
    setIsUploading(false);
    setUploadProgress(0);
    setImgVersion((value) => value + 1);
  }, [isOpen, adherent]);

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;

    if (name === "date_naissance") {
      setInvalidBirthDate(!isValidDateInput(value));
    }

    if (name === "photo_url") {
      setImgOk(true);
      setImgVersion((version) => version + 1);
    }

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const openFilePicker = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];

    if (!file) return;

    if (!adherent?.id) {
      toast.error("ID adhérent manquant.");
      return;
    }

    try {
      setIsUploading(true);
      setUploadProgress(0);

      const response = await uploadAdherentPhoto(adherent.id, file, {
        format: outputFormat,
        onUploadProgress: (progressEvent) => {
          if (!progressEvent.total) return;

          const percent = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );

          setUploadProgress(percent);
        },
      });

      const newUrl =
        response?.photo_url ||
        response?.photoURL ||
        response?.imageUrl ||
        response?.url ||
        response?.path ||
        response?.data?.photo_url ||
        response?.data?.photoURL ||
        response?.data?.imageUrl ||
        response?.data?.url ||
        response?.data?.path ||
        "";

      if (!newUrl) {
        toast.warn("Upload terminé, mais aucune URL n'a été renvoyée.");
        return;
      }

      setFormData((prev) => ({
        ...prev,
        photo_url: newUrl,
      }));

      setImgOk(true);
      setImgVersion((version) => version + 1);

      toast.success("Photo uploadée avec succès.");

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (error) {
      console.error("Erreur upload photo adhérent :", error);
      toast.error(`Erreur upload : ${error?.message || "inconnue"}`);
    } finally {
      setIsUploading(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!adherent?.id) {
      toast.error("ID adhérent manquant.");
      return;
    }

    if (invalidBirthDate && formData.date_naissance) {
      toast.error("La date de naissance est invalide.");
      return;
    }

    if (isUploading) {
      toast.info("L'upload de la photo est encore en cours.");
      return;
    }

    try {
      setSaving(true);

      await updateAdherent(adherent.id, { ...formData });

      toast.success("Adhérent mis à jour avec succès.");

      await refreshList?.();
      onClose?.();
    } catch (error) {
      console.error("Erreur mise à jour adhérent :", error);
      toast.error("Erreur lors de la mise à jour de l'adhérent.");
    } finally {
      setSaving(false);
    }
  };

  const absolutePhoto = toAbsolutePhotoUrl(formData.photo_url);
  const previewSrc = formData.photo_url
    ? withBust(absolutePhoto, imgVersion)
    : withBust("/img/user_avatar.jpg", imgVersion);

  return (
    <>
      <AdherentDrawer
        isOpen={isOpen}
        onClose={onClose}
        title="Modifier un adhérent"
        subtitle={
          adherent
            ? `${adherent.nom || ""} ${adherent.prenom || ""}`.trim()
            : "Mettez à jour les informations de l'adhérent."
        }
        width="max-w-5xl"
      >
        <form onSubmit={handleSubmit} className="flex min-h-full flex-col">
          <div className="flex-1 space-y-5 p-4 sm:p-6">
            <SectionCard
              icon={<FaImage />}
              title="État civil & photo"
              subtitle="Identité, licence, naissance, poids et avatar de l'adhérent."
            >
              <div className="grid gap-5 lg:grid-cols-[1fr_280px]">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <FieldLabel>Nom</FieldLabel>
                    <input
                      type="text"
                      name="nom"
                      value={formData.nom}
                      onChange={handleChange}
                      required
                      className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                    />
                  </div>

                  <div>
                    <FieldLabel>Prénom</FieldLabel>
                    <input
                      type="text"
                      name="prenom"
                      value={formData.prenom}
                      onChange={handleChange}
                      required
                      className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                    />
                  </div>

                  <div>
                    <FieldLabel>Genre</FieldLabel>
                    <select
                      name="genre"
                      value={formData.genre}
                      onChange={handleChange}
                      className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
                    >
                      <option value="">— Sélectionner —</option>
                      <option value="Masculin">Masculin</option>
                      <option value="Féminin">Féminin</option>
                    </select>
                  </div>

                  <div>
                    <FieldLabel>Date de naissance</FieldLabel>
                    <input
                      type="date"
                      name="date_naissance"
                      value={formData.date_naissance}
                      onChange={handleChange}
                      aria-invalid={invalidBirthDate ? "true" : "false"}
                      className={`w-full rounded-2xl border bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:ring-2 dark:bg-slate-950 dark:text-white ${
                        invalidBirthDate
                          ? "border-rose-400 focus:ring-rose-400 dark:border-rose-500"
                          : "border-slate-200 focus:ring-indigo-400 dark:border-slate-700"
                      }`}
                    />

                    {invalidBirthDate ? (
                      <p className="mt-1 text-xs text-rose-600 dark:text-rose-400">
                        Date invalide à corriger.
                      </p>
                    ) : null}
                  </div>

                  <div>
                    <FieldLabel>Lieu de naissance</FieldLabel>
                    <input
                      type="text"
                      name="lieu_naissance"
                      value={formData.lieu_naissance}
                      onChange={handleChange}
                      className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                    />
                  </div>

                  <div>
                    <FieldLabel>Numéro de licence</FieldLabel>
                    <input
                      type="text"
                      name="licence_number"
                      value={formData.licence_number}
                      onChange={handleChange}
                      className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                    />
                  </div>

                  <div>
                    <FieldLabel>Poids kg</FieldLabel>
                    <input
                      type="number"
                      step="0.1"
                      name="poids"
                      value={formData.poids}
                      onChange={handleChange}
                      className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                    />
                  </div>

                  <div className="sm:col-span-2">
                    <FieldLabel>URL photo</FieldLabel>

                    <div className="mb-2 flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => setUploadMode("url")}
                        className={`inline-flex items-center gap-2 rounded-2xl px-3 py-2 text-xs font-bold transition ${
                          uploadMode === "url"
                            ? "bg-indigo-600 text-white"
                            : "border border-slate-200 bg-white text-slate-700 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
                        }`}
                      >
                        <FaLink />
                        URL
                      </button>

                      <button
                        type="button"
                        onClick={() => setUploadMode("upload")}
                        className={`inline-flex items-center gap-2 rounded-2xl px-3 py-2 text-xs font-bold transition ${
                          uploadMode === "upload"
                            ? "bg-indigo-600 text-white"
                            : "border border-slate-200 bg-white text-slate-700 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
                        }`}
                      >
                        <FaCloudUploadAlt />
                        Upload
                      </button>
                    </div>

                    {uploadMode === "url" ? (
                      <input
                        type="text"
                        name="photo_url"
                        value={formData.photo_url}
                        onChange={handleChange}
                        placeholder="https://... ou /uploads/..."
                        className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                      />
                    ) : (
                      <div className="rounded-2xl border border-slate-200 bg-white p-3 dark:border-slate-700 dark:bg-slate-950">
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/*"
                          onChange={handleFileChange}
                          className="hidden"
                        />

                        <div className="flex flex-wrap items-center gap-2">
                          <button
                            type="button"
                            onClick={openFilePicker}
                            disabled={isUploading}
                            className="inline-flex items-center gap-2 rounded-2xl bg-indigo-600 px-3 py-2 text-sm font-bold text-white transition hover:bg-indigo-700 disabled:opacity-60"
                          >
                            <FaCloudUploadAlt />
                            Choisir un fichier
                          </button>

                          <select
                            value={outputFormat}
                            onChange={(event) => setOutputFormat(event.target.value)}
                            disabled={isUploading}
                            className="rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
                          >
                            <option value="webp">WEBP</option>
                            <option value="jpg">JPG</option>
                            <option value="png">PNG</option>
                          </select>
                        </div>

                        {isUploading ? (
                          <div className="mt-3">
                            <div className="h-2 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800">
                              <div
                                className="h-full rounded-full bg-indigo-600"
                                style={{ width: `${uploadProgress}%` }}
                              />
                            </div>

                            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                              Upload : {uploadProgress}%
                            </p>
                          </div>
                        ) : null}

                        <p className="mt-2 text-xs leading-5 text-slate-500 dark:text-slate-400">
                          L'image sera optimisée côté serveur.
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="rounded-[24px] border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950">
                  <p className="text-sm font-bold text-slate-900 dark:text-white">
                    Aperçu photo
                  </p>

                  <div className="mt-3 aspect-square overflow-hidden rounded-[24px] border border-slate-200 bg-slate-100 dark:border-slate-800 dark:bg-slate-900">
                    <img
                      key={imgVersion}
                      src={previewSrc}
                      alt="Aperçu adhérent"
                      onError={() => setImgOk(false)}
                      className="h-full w-full object-cover"
                    />
                  </div>

                  {!imgOk ? (
                    <p className="mt-2 text-xs text-rose-600 dark:text-rose-400">
                      Impossible de charger l'image.
                    </p>
                  ) : null}

                  {formData.photo_url ? (
                    <button
                      type="button"
                      onClick={() => setIsCropOpen(true)}
                      className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                    >
                      <FaCropAlt />
                      Recadrer / Zoom
                    </button>
                  ) : null}
                </div>
              </div>
            </SectionCard>

            <SectionCard
              icon={<FaImage />}
              title="Coordonnées"
              subtitle="Emails, téléphones, adresse et représentant légal."
            >
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <FieldLabel>Email</FieldLabel>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                  />
                </div>

                <div>
                  <FieldLabel>Email secondaire</FieldLabel>
                  <input
                    type="email"
                    name="email_secondaire"
                    value={formData.email_secondaire}
                    onChange={handleChange}
                    className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                  />
                </div>

                <div>
                  <FieldLabel>Téléphone</FieldLabel>
                  <input
                    type="text"
                    name="tel"
                    value={formData.tel}
                    onChange={handleChange}
                    className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                  />
                </div>

                <div>
                  <FieldLabel>Téléphone secondaire</FieldLabel>
                  <input
                    type="text"
                    name="tel_secondaire"
                    value={formData.tel_secondaire}
                    onChange={handleChange}
                    className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                  />
                </div>

                <div className="sm:col-span-2">
                  <FieldLabel>Représentant légal</FieldLabel>
                  <input
                    type="text"
                    name="representant_legal"
                    value={formData.representant_legal}
                    onChange={handleChange}
                    className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                  />
                </div>

                <div className="sm:col-span-2">
                  <FieldLabel>Adresse</FieldLabel>
                  <input
                    type="text"
                    name="adresse"
                    value={formData.adresse}
                    onChange={handleChange}
                    className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                  />
                </div>

                <div>
                  <FieldLabel>Code postal</FieldLabel>
                  <input
                    type="text"
                    name="cp"
                    value={formData.cp}
                    onChange={handleChange}
                    className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                  />
                </div>

                <div>
                  <FieldLabel>Commune</FieldLabel>
                  <input
                    type="text"
                    name="commune"
                    value={formData.commune}
                    onChange={handleChange}
                    className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                  />
                </div>
              </div>
            </SectionCard>

            <SectionCard
              icon={<FaImage />}
              title="Infos club"
              subtitle="Statut, saison, Pass'Sport, autorisation photo et notes."
            >
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <FieldLabel>Statut d'inscription</FieldLabel>
                  <select
                    name="statut_inscription"
                    value={formData.statut_inscription}
                    onChange={handleChange}
                    className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
                  >
                    <option value="">— Sélectionner —</option>
                    <option value="Licencié">Licencié</option>
                    <option value="Inscrit">Inscrit</option>
                    <option value="Cours d'essai">Cours d'essai</option>
                    <option value="Licencié - Arrêt">Licencié - Arrêt</option>
                    <option value="Non Licencié - Arrêt">Non Licencié - Arrêt</option>
                    <option value="Licencié Hors Club">Licencié Hors Club</option>
                    <option value="Ancien Licencié">Ancien Licencié</option>
                    <option value="Ancien Licencié et Arrêt">
                      Ancien Licencié et Arrêt
                    </option>
                  </select>
                </div>

                <div>
                  <FieldLabel>Saison active</FieldLabel>
                  <select
                    name="saison_active"
                    value={formData.saison_active}
                    onChange={handleChange}
                    className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
                  >
                    <option value="">— Sélectionner —</option>
                    {seasons.map((season) => (
                      <option key={season} value={season}>
                        {season}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <FieldLabel>N° Pass'Sport</FieldLabel>
                  <input
                    type="text"
                    name="passsport_number"
                    value={formData.passsport_number}
                    onChange={handleChange}
                    className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                  />
                </div>

                <div>
                  <FieldLabel>Date création</FieldLabel>
                  <input
                    type="date"
                    name="createdAt"
                    value={formData.createdAt}
                    onChange={handleChange}
                    className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                  />
                </div>

                <div className="flex items-end">
                  <label className="inline-flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200">
                    <input
                      type="checkbox"
                      name="autorisation_photo"
                      checked={formData.autorisation_photo}
                      onChange={handleChange}
                      className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-400"
                    />
                    Autorisation photo
                  </label>
                </div>

                <div className="sm:col-span-2">
                  <FieldLabel>Notes</FieldLabel>
                  <textarea
                    name="notes_adherent"
                    value={formData.notes_adherent}
                    onChange={handleChange}
                    rows={4}
                    className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                  />
                </div>
              </div>
            </SectionCard>
          </div>

          <div className="flex justify-end gap-2 border-t border-slate-200 p-4 dark:border-slate-800 sm:px-6">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
            >
              <FaTimes />
              Annuler
            </button>

            <button
              type="submit"
              disabled={saving || isUploading}
              className="inline-flex items-center gap-2 rounded-2xl bg-indigo-600 px-4 py-2 text-sm font-bold text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <FaSave />
              {saving ? "Enregistrement..." : "Mettre à jour"}
            </button>
          </div>
        </form>
      </AdherentDrawer>

      <CropPhotoModal
        isOpen={isCropOpen}
        onClose={() => setIsCropOpen(false)}
        entityId={adherent?.id}
        imageUrl={
          formData.photo_url
            ? `${toAbsolutePhotoUrl(toMasterUrl(formData.photo_url))}?v=${imgVersion}`
            : ""
        }
        onCropped={(newUrl) => {
          setFormData((prev) => ({
            ...prev,
            photo_url: newUrl,
          }));
          setImgVersion((version) => version + 1);
        }}
        format="webp"
        cropAction={cropAdherentPhoto}
      />
    </>
  );
}